import React, { useEffect, useState, useRef } from "react";
import { Alert, Table, Form, Row, Col, Button } from "react-bootstrap";
import api from "../axiosConfig";
import "./dashboard.css";

const DeudoresAlertAvanzado = () => {
  const [deudores, setDeudores] = useState([]);
  const [prevIds, setPrevIds] = useState([]);
  const [deudoresNuevosIds, setDeudoresNuevosIds] = useState([]);
  const [desapareciendoIds, setDesapareciendoIds] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const pageSize = 10;
  const audioRef = useRef(null);

  // Filtros
  const [filtroCliente, setFiltroCliente] = useState("");
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [montoMin, setMontoMin] = useState("");

  // Estado de exportación
  const [exportando, setExportando] = useState(false);

  const fetchDeudores = async () => {
    try {
      const res = await api.get("/reportes/deudores", {
        params: {
          cliente: filtroCliente || undefined,
          start_date: fechaInicio || undefined,
          end_date: fechaFin || undefined,
          monto_min: montoMin || undefined,
          page,
          page_size: pageSize,
        },
      });

      const nuevosDeudores = res.data.deudores;
      setTotal(res.data.total);

      const nuevosIdsStr = nuevosDeudores.map((d) => String(d.id_pago));
      const prevIdsStr = prevIds.map(String);

      // Detectar deudores nuevos
      const idsNuevos = nuevosIdsStr.filter((id) => !prevIdsStr.includes(id));
      if (idsNuevos.length > 0) {
        setDeudoresNuevosIds(idsNuevos);
        if (audioRef.current) audioRef.current.play();
        setTimeout(() => setDeudoresNuevosIds([]), 3000);
      }

      // Siempre actualizar prevIds
      setPrevIds(nuevosIdsStr);

      // Detectar deudores que desaparecen (pagaron)
      const idsQueDesaparecen = deudores
        .map((d) => d.id_pago)
        .filter((id) => !nuevosIdsStr.includes(id));
      if (idsQueDesaparecen.length > 0) {
        setDesapareciendoIds(idsQueDesaparecen);
        setTimeout(() => {
          setDeudores(nuevosDeudores);
          setDesapareciendoIds([]);
          setPrevIds(nuevosIdsStr);
        }, 1000); // duración animación fade-out
      } else {
        setDeudores(nuevosDeudores);
        setPrevIds(nuevosIdsStr);
      }
    } catch (error) {
      console.error("Error al cargar deudores:", error);
    }
  };

  useEffect(() => {
    fetchDeudores();
    const interval = setInterval(fetchDeudores, 15 * 60 * 1000); //suene cada 15min
    return () => clearInterval(interval);
  }, [filtroCliente, fechaInicio, fechaFin, montoMin, page]);

  const aplicarFiltros = () => setPage(1) || fetchDeudores();
  const limpiarFiltros = () => {
    setFiltroCliente("");
    setFechaInicio("");
    setFechaFin("");
    setMontoMin("");
    setPage(1);
    fetchDeudores();
  };

  const totalPages = Math.ceil(total / pageSize);

  const exportarExcel = async () => {
    try {
      setExportando(true);
      const response = await api.get("/reportes/deudores-excel", {
        params: {
          cliente: filtroCliente || undefined,
          start_date: fechaInicio || undefined,
          end_date: fechaFin || undefined,
          monto_min: montoMin || undefined,
        },
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "deudores.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error exportando Excel:", error);
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="container mt-3">
      {/* ALERTA PERMANENTE */}
      {total > 0 && (
        <Alert variant="danger">
          ¡Tienes {total} clientes con pagos pendientes!
        </Alert>
      )}

      <audio ref={audioRef} src="/alerta.mp3" preload="auto" />

      {/* FILTROS */}
      <div className="card p-3 shadow-sm mb-3">
        <h6>Filtros de deudores</h6>
        <Row className="align-items-end">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Cliente</Form.Label>
              <Form.Control
                type="text"
                value={filtroCliente}
                onChange={(e) => setFiltroCliente(e.target.value)}
                placeholder="Nombre o apellido"
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Fecha inicio</Form.Label>
              <Form.Control
                type="date"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Fecha fin</Form.Label>
              <Form.Control
                type="date"
                value={fechaFin}
                onChange={(e) => setFechaFin(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Monto mínimo</Form.Label>
              <Form.Control
                type="number"
                value={montoMin}
                onChange={(e) => setMontoMin(e.target.value)}
                placeholder="0"
              />
            </Form.Group>
          </Col>
          <Col md={1}>
            <Button variant="primary" onClick={aplicarFiltros}>
              Aplicar
            </Button>
            <Button
              variant="secondary"
              className="mt-2"
              onClick={limpiarFiltros}
            >
              Limpiar
            </Button>
          </Col>
        </Row>
      </div>

      {/* TABLA DE DEUDORES */}
      {deudores.length > 0 && (
        <div className="card p-3 shadow-sm mt-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="text-primary">Clientes Deudores</h5>
            <Button
              variant="success"
              disabled={exportando}
              onClick={exportarExcel}
            >
              {exportando ? "Exportando..." : "Exportar a Excel"}
            </Button>
          </div>

          <div className="table-responsive">
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Monto Pendiente</th>
                  <th>Fecha de Pago</th>
                  <th>Estado</th>
                </tr>
              </thead>
              <tbody>
                {deudores.map((d) => {
                  const esNuevo = deudoresNuevosIds.includes(d.id_pago);
                  const seVa = desapareciendoIds.includes(d.id_pago);
                  const clases = seVa
                    ? "fade-out"
                    : esNuevo
                    ? "parpadeo"
                    : d.monto >= 500
                    ? "table-danger"
                    : "";

                  return (
                    <tr key={d.id_pago} className={clases}>
                      <td>{d.cliente}</td>
                      <td>${d.monto}</td>
                      <td>{new Date(d.fecha_pago).toLocaleDateString()}</td>
                      <td>{d.estado}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>

          {/* BOTONES DE PAGINACIÓN */}
          <div className="d-flex justify-content-between mt-2">
            <Button disabled={page <= 1} onClick={() => setPage((prev) => prev - 1)}>
              Anterior
            </Button>
            <span>
              Página {page} de {totalPages}
            </span>
            <Button
              disabled={page >= totalPages}
              onClick={() => setPage((prev) => prev + 1)}
            >
              Siguiente
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeudoresAlertAvanzado;